/*
 * Client-side CSV export, pure (no DOM, no network). Shared by every "Download CSV" button.
 * Output: UTF-8 BOM (Excel), RFC 4180 quoting, CRLF rows, CSV-injection guard, money as plain
 * rupees with 2 decimals, instants as IST "YYYY-MM-DD HH:mm".
 */
import { IST_OFFSET_MS } from '../../views/teacher_payments/shared/ist';

export const BOM = '﻿';
const pad = (n) => String(n).padStart(2, '0');

// A plain number ("-120.50", "3", "1e3" excluded) stays numeric; the guard must not quote it.
const PLAIN_NUMBER_RE = /^-?\d+(\.\d+)?$/;
// Spreadsheet formula triggers (OWASP CSV injection list).
const FORMULA_START_RE = /^[=+\-@\t\r]/;

// '=SUM(A1)' -> "'=SUM(A1)"; '-120.50' stays.
export const guard_formula = (s) =>
  FORMULA_START_RE.test(s) && !PLAIN_NUMBER_RE.test(s) ? `'${s}` : s;

// RFC 4180: quote when the cell holds a comma, quote, CR or LF; double inner quotes.
export const quote_cell = (s) => (/[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

const to_number = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

// 2-decimal rupees, half away from zero, no "-0.00". '' when not a number.
export const money_text = (v) => {
  const n = to_number(v);
  if (n === null) return '';
  const r = Math.sign(n) * Math.round(Math.abs(n) * 100 + 1e-9);
  return r === 0 ? '0.00' : (r / 100).toFixed(2);
};

const ZONELESS_RE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(:\d{2}(\.\d+)?)?$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/*
 * An instant as IST "YYYY-MM-DD HH:mm". Numbers are epoch (seconds below 1e12, else ms); strings
 * with Z/offset are converted; zoneless "YYYY-MM-DD HH:mm[:ss]" is already IST (the goserver
 * convention) and only trimmed; a bare day passes through. '' when unreadable.
 */
export const ist_datetime_text = (v) => {
  if (v === null || v === undefined || v === '') return '';
  let ms;
  if (v instanceof Date) ms = v.getTime();
  else if (typeof v === 'number') ms = v < 1e12 ? v * 1000 : v;
  else {
    const s = String(v).trim();
    if (DAY_RE.test(s)) return s;
    const z = ZONELESS_RE.exec(s);
    if (z) return `${z[1]} ${z[2]}:${z[3]}`;
    ms = Date.parse(s);
  }
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms + IST_OFFSET_MS);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
    d.getUTCHours()
  )}:${pad(d.getUTCMinutes())}`;
};

// An IST calendar day "YYYY-MM-DD" from a day string or an instant.
export const ist_date_text = (v) => ist_datetime_text(v).slice(0, 10);

/*
 * One cell's text before quoting. kind: 'text' (default) | 'number' | 'money' (rupees) |
 * 'paise' (integer paise -> rupees) | 'datetime' | 'date' | 'bool' (Yes/No).
 */
export const cell_text = (v, kind = 'text') => {
  switch (kind) {
    case 'money':
      return money_text(v);
    case 'paise': {
      const n = to_number(v);
      return n === null ? '' : money_text(n / 100);
    }
    case 'number': {
      const n = to_number(v);
      return n === null ? '' : String(n);
    }
    case 'datetime':
      return ist_datetime_text(v);
    case 'date':
      return ist_date_text(v);
    case 'bool':
      return v === null || v === undefined || v === '' ? '' : v ? 'Yes' : 'No';
    default:
      if (v === null || v === undefined) return '';
      if (Array.isArray(v)) return v.join('; ');
      return String(v);
  }
};

const read = (col, row) =>
  typeof col.value === 'function' ? col.value(row) : row == null ? undefined : row[col.key];

/*
 * columns: [{ header, key | value(row), kind }]. Returns the whole file text (BOM + header + rows,
 * CRLF-terminated). bom:false for tests that compare bare text.
 */
export const to_csv = (columns, rows, { bom = true } = {}) => {
  const line = (cells) => cells.map((c) => quote_cell(guard_formula(c))).join(',');
  const out = [line(columns.map((c) => String(c.header)))];
  (rows || []).forEach((row) => {
    out.push(line(columns.map((c) => cell_text(read(c, row), c.kind))));
  });
  return (bom ? BOM : '') + out.join('\r\n') + '\r\n';
};

// The header + mapped values without CSV encoding: what per-table tests assert against.
export const to_records = (columns, rows) =>
  (rows || []).map((row) => {
    const rec = {};
    columns.forEach((c) => {
      rec[c.header] = cell_text(read(c, row), c.kind);
    });
    return rec;
  });

/*
 * PII never leaves in a CSV: bank account, IFSC, PAN, GSTIN, email, phone. Per-table tests call
 * this on their column list; the button refuses to export when it finds any.
 */
export const PII_COLUMN_RE =
  /(^|[^a-z])(account[ _-]?(no|num|number)|acct|bank[ _-]?account|ifsc|pan|gstin|e-?mail|phone|mobile|whatsapp|upi)([^a-z]|$)/i;

export const pii_columns = (columns) =>
  columns
    .filter((c) => PII_COLUMN_RE.test(String(c.header)) || (c.key && PII_COLUMN_RE.test(c.key)))
    .map((c) => c.header);

const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// "<page>-<table>-<period>-<YYYYMMDD-HHmm IST>.csv"; period is the pay month / day, omitted when empty.
export const csv_file_name = ({ page, table, period, now = new Date() }) => {
  const d = new Date(now.getTime() + IST_OFFSET_MS);
  const stamp = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}-${pad(
    d.getUTCHours()
  )}${pad(d.getUTCMinutes())}`;
  const parts = [slug(page), slug(table), slug(period ? String(period).slice(0, 10) : '')].filter(
    Boolean
  );
  return `${parts.join('-')}-${stamp}.csv`;
};
